import { Link, useRouterState } from '@tanstack/react-router';
import {
  ActivityIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  BookOpenIcon,
  CheckIcon,
  FileCodeIcon,
  GitBranchIcon,
  KeyRoundIcon,
  ListTreeIcon,
  PackageIcon,
  PlusIcon,
  RepeatIcon,
  ScanLineIcon,
  ServerIcon,
  SparklesIcon,
  TimerIcon,
  TrendingDownIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function LandingPage() {
  const hash = useRouterState({ select: (s) => s.location.hash });
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [hash]);

  return (
    <>
      <Hero />
      <Problem />
      <Dimensions />
      <HowItWorks />
      <SampleReport />
      <RoiCalculator />
      <Pricing />
      <Faq />
      <ClosingCta />
    </>
  );
}

/* ---------- Hero ---------- */

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(37,99,235,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pt-16 pb-20 md:grid-cols-2 md:pt-24 md:pb-28">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-400">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
            A product by Momentum AI
          </div>
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-[64px]">
            Your API was built for humans.
            <br />
            <span className="text-[var(--color-accent)]">Your next integration won't be.</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
            Athena audits how well AI coding agents — Cursor, Claude Code, Copilot — can actually
            integrate with your API. Then it tells you exactly what to fix.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <Link to="/signup">
                Get a free audit of your API
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </Button>
            <Link
              to="/sample-report"
              className="inline-flex items-center gap-1.5 px-2 py-1 text-sm font-medium text-slate-700 hover:text-[var(--color-accent)] dark:text-slate-300 dark:hover:text-[var(--color-accent)]"
            >
              See a sample report
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <ReportMockup />
      </div>
    </section>
  );
}

function ReportMockup() {
  const bars: Array<{ label: string; value: number; tone: 'good' | 'mid' | 'bad' }> = [
    { label: 'Time to first call', value: 85, tone: 'good' },
    { label: 'OpenAPI spec quality', value: 78, tone: 'good' },
    { label: 'llms.txt coverage', value: 12, tone: 'bad' },
    { label: 'Docs structure', value: 62, tone: 'mid' },
    { label: 'SDK consistency', value: 55, tone: 'mid' },
    { label: 'Error documentation', value: 34, tone: 'bad' },
    { label: 'Authentication clarity', value: 80, tone: 'good' },
    { label: 'MCP server availability', value: 0, tone: 'bad' },
  ];
  const toneClass = (t: 'good' | 'mid' | 'bad') =>
    t === 'good' ? 'bg-emerald-500' : t === 'mid' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="relative hidden md:block">
      <div className="absolute -inset-8 rounded-3xl bg-gradient-to-br from-[var(--color-accent)]/10 to-transparent blur-2xl" />
      <div className="relative rotate-[-2deg] transition-transform duration-500 hover:rotate-0">
        <div className="mx-auto aspect-[8.5/11] max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-100 p-6 dark:border-slate-800">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                Athena Audit
              </p>
              <p className="mt-0.5 font-mono text-[10px] text-slate-400">api.example.com</p>
            </div>
            <div className="font-mono text-[10px] text-slate-400">12 / 04 / 2026</div>
          </div>
          <div className="p-6">
            <p className="mb-1 text-xs text-slate-500">Overall score</p>
            <div className="mb-6 flex items-baseline gap-2">
              <span className="font-mono text-5xl font-bold">67</span>
              <span className="font-mono text-lg text-slate-400">/ 100</span>
              <span className="ml-auto rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                Needs work
              </span>
            </div>
            <div className="space-y-2.5">
              {bars.map((b) => (
                <div key={b.label} className="flex items-center gap-3 text-xs">
                  <span className="w-36 truncate text-slate-600 dark:text-slate-400">{b.label}</span>
                  <div className="h-1.5 flex-1 rounded bg-slate-100 dark:bg-slate-800">
                    <div
                      className={cn('h-full rounded', toneClass(b.tone))}
                      style={{ width: `${b.value}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-mono text-slate-500">{b.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-slate-100 pt-4 font-mono text-[10px] text-slate-400 dark:border-slate-800">
              page 3 of 12 · full report ↓
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Problem ---------- */

function Problem() {
  const items = [
    {
      icon: ScanLineIcon,
      title: "Agents don't read your docs — they parse them.",
      body: 'The gap between human-readable and agent-readable is where integrations fail.',
    },
    {
      icon: GitBranchIcon,
      title: 'Your SDKs drift in silence.',
      body: 'Python returns an iterator, Node returns a cursor — agents generate code that\'s subtly wrong across languages.',
    },
    {
      icon: TrendingDownIcon,
      title: 'Activation stalls, support tickets spike.',
      body: 'When the first integration attempt fails, your trial-to-paid conversion takes the hit.',
    },
  ];
  return (
    <section
      id="problem"
      className="scroll-mt-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="mb-12 max-w-2xl">
          <SectionEyebrow>The problem</SectionEyebrow>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            In 2026, most integrations are written by agents — not humans.
          </h2>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {items.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-lg border border-slate-200 p-6 transition-colors hover:border-[var(--color-accent)]/60 dark:border-slate-800 dark:hover:border-[var(--color-accent)]/60"
            >
              <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-md bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">{title}</h3>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Dimensions ---------- */

function Dimensions() {
  const dims = [
    {
      n: '01',
      icon: TimerIcon,
      title: 'Time to first call',
      body: 'Can an agent go from landing on your docs to a working API call in under five minutes?',
    },
    {
      n: '02',
      icon: FileCodeIcon,
      title: 'OpenAPI spec quality',
      body: 'Is your spec complete, current, and parseable? Missing examples and undocumented error shapes are where agents hallucinate.',
    },
    {
      n: '03',
      icon: ListTreeIcon,
      title: 'llms.txt coverage',
      body: "A structured index of your docs built specifically for LLMs. Most APIs don't have one.",
    },
    {
      n: '04',
      icon: BookOpenIcon,
      title: 'Docs structure',
      body: 'Is your documentation organized so an agent can find what it needs without scraping the whole site?',
    },
    {
      n: '05',
      icon: PackageIcon,
      title: 'SDK consistency',
      body: 'Do your SDKs follow the same patterns across languages? Inconsistency breaks cross-language code generation.',
    },
    {
      n: '06',
      icon: AlertTriangleIcon,
      title: 'Error documentation',
      body: 'Are error responses documented with real example bodies? This is the top source of agent-generated bugs.',
    },
    {
      n: '07',
      icon: KeyRoundIcon,
      title: 'Authentication clarity',
      body: 'Can an agent set up auth correctly on the first try, including edge cases like token refresh?',
    },
    {
      n: '08',
      icon: ServerIcon,
      title: 'MCP server availability',
      body: 'Do you publish a Model Context Protocol server so agents can interact with your API directly?',
    },
    {
      n: '09',
      icon: SparklesIcon,
      title: 'Agent skills & rules',
      body: 'Have you published Cursor rules, Copilot instructions, or Claude skills for your API?',
    },
    {
      n: '10',
      icon: ActivityIcon,
      title: 'Drift & versioning',
      body: 'When your API changes, do your docs, SDKs, and AI assets stay in sync?',
    },
  ];

  return (
    <section
      id="dimensions"
      className="scroll-mt-20 border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="mb-12 max-w-3xl">
          <SectionEyebrow>Methodology</SectionEyebrow>
          <h2 className="text-3xl font-bold tracking-tight md:text-5xl">
            10 dimensions of AI-agent readiness.
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
            Every audit scores your API across ten independent surfaces. Here's what we measure,
            and why each one matters.
          </p>
        </div>

        <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800 md:grid-cols-2">
          {dims.map(({ n, icon: Icon, title, body }) => (
            <div
              key={n}
              className="flex gap-4 bg-white p-6 dark:bg-slate-950"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="mb-1 flex items-baseline gap-2">
                  <span className="font-mono text-xs text-slate-400">{n}</span>
                  <h3 className="font-semibold">{title}</h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/methodology"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-accent)] hover:underline"
          >
            Read the full methodology
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- How it works ---------- */

function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Point us at your API.',
      body: 'Give us your docs URL and primary OpenAPI spec. We handle the rest.',
    },
    {
      n: '02',
      title: 'We run the audit.',
      body: 'Our system probes your API surface across all 10 dimensions — about 15 minutes.',
    },
    {
      n: '03',
      title: 'You get a 12-page report.',
      body: 'Exact findings, evidence, and prioritized fixes. Polished PDF, scored out of 100.',
    },
    {
      n: '04',
      title: 'We auto-generate the missing artifacts.',
      body: 'Same run scaffolds the AI-readiness assets you’re missing — llms.txt, MCP server, Cursor and Claude rules — from your spec and docs, ready to publish.',
    },
  ];

  return (
    <section
      id="how-it-works"
      className="scroll-mt-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="mb-12 max-w-2xl">
          <SectionEyebrow>How it works</SectionEyebrow>
          <h2 className="text-3xl font-bold tracking-tight md:text-5xl">
            From URL to report in 15 minutes.
          </h2>
        </div>

        <div className="relative grid gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-4">
          <div className="absolute top-6 left-[16%] right-[16%] hidden h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent md:block dark:via-slate-700" />
          {steps.map(({ n, title, body }) => (
            <div key={n} className="relative">
              <div className="relative z-10 mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-[var(--color-accent)] font-mono font-bold text-white">
                {n}
              </div>
              <h3 className="mb-2 text-lg font-semibold">{title}</h3>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-4 rounded-lg border border-[var(--color-accent)]/20 bg-[var(--color-accent)]/5 p-6 md:flex-row md:items-center md:p-8 dark:bg-[var(--color-accent)]/10">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--color-accent)] text-white">
            <RepeatIcon className="h-5 w-5" />
          </div>
          <p className="flex-1 text-slate-800 dark:text-slate-200">
            <span className="font-semibold">Want continuous monitoring?</span> Subscribe to catch
            drift on every PR, auto-generate missing AI assets, and track your score over time.
          </p>
          <a
            href="#pricing"
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[var(--color-accent)] hover:underline"
          >
            See plans
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- Sample report ---------- */

function SampleReport() {
  return (
    <section
      id="sample"
      className="scroll-mt-20 border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 md:grid-cols-2 md:py-28">
        <div>
          <SectionEyebrow>Sample report</SectionEyebrow>
          <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-5xl">
            12 pages. Every number backed by evidence.
          </h2>
          <p className="mb-7 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
            Every audit produces a 12-page report: executive summary, scored breakdown by
            dimension, concrete evidence with code snippets, and prioritized next steps. Here's
            what a real one looks like.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/sample-report">
              <ArrowRightIcon className="h-4 w-4" />
              See a sample report
            </Link>
          </Button>
        </div>

        <div className="relative h-[420px] md:h-[500px]">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-60 md:w-72">
              <div className="absolute top-4 left-4 aspect-[8.5/11] w-full rotate-6 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900" />
              <div className="absolute top-2 left-2 aspect-[8.5/11] w-full rotate-3 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900" />
              <div className="relative aspect-[8.5/11] w-full rounded-lg border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-3 h-2 w-16 rounded bg-[var(--color-accent)]" />
                <div className="mb-2 h-3 w-3/4 rounded bg-slate-900 dark:bg-slate-100" />
                <div className="mb-6 h-3 w-1/2 rounded bg-slate-900 dark:bg-slate-100" />
                <div className="mb-5 space-y-1.5">
                  <div className="h-1.5 w-full rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-1.5 w-5/6 rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-1.5 w-full rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-1.5 w-4/6 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
                <div className="mb-4 rounded bg-slate-900 p-3 font-mono text-[8px] text-emerald-400 dark:bg-slate-800">
                  <div>$ GET /api/v1/users</div>
                  <div className="text-red-400">→ 400 missing example body</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded bg-slate-100 p-2 dark:bg-slate-800">
                    <div className="font-mono text-[9px] text-slate-500">SCORE</div>
                    <div className="font-mono text-lg font-bold">67</div>
                  </div>
                  <div className="rounded bg-[var(--color-accent)]/10 p-2">
                    <div className="font-mono text-[9px] text-[var(--color-accent)]">FIXES</div>
                    <div className="font-mono text-lg font-bold text-[var(--color-accent)]">14</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- ROI Calculator ---------- */

type RoiTab = 'revenue' | 'support';

function RoiCalculator() {
  const [tab, setTab] = useState<RoiTab>('revenue');

  return (
    <section
      id="roi"
      className="scroll-mt-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="mb-8 max-w-2xl">
          <SectionEyebrow>ROI</SectionEyebrow>
          <h2 className="text-3xl font-bold tracking-tight md:text-5xl">Calculate your ROI.</h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
            Estimate the revenue you're leaving on the table when AI agents fail to integrate, and
            how much support volume you can deflect by making your API agent-ready.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div
            role="tablist"
            aria-label="ROI calculator"
            className="flex border-b border-slate-200 dark:border-slate-800"
          >
            <TabButton selected={tab === 'revenue'} onClick={() => setTab('revenue')}>
              Revenue
            </TabButton>
            <TabButton selected={tab === 'support'} onClick={() => setTab('support')}>
              Support cost
            </TabButton>
          </div>

          <div className="p-6 md:p-8">
            {tab === 'revenue' ? <RevenuePanel /> : <SupportPanel />}
          </div>
        </div>
      </div>
    </section>
  );
}

function TabButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        'relative px-4 py-4 text-sm font-medium transition-colors sm:px-6',
        selected
          ? 'text-slate-900 dark:text-slate-100'
          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
      )}
    >
      {children}
      {selected && (
        <span className="absolute inset-x-4 bottom-0 h-0.5 bg-[var(--color-accent)]" />
      )}
    </button>
  );
}

function RevenuePanel() {
  const [calls, setCalls] = useState(5_000_000);
  const [revPerCall, setRevPerCall] = useState(0.02);
  const [missRate, setMissRate] = useState(0.4);
  const [recovery, setRecovery] = useState(0.9);

  const { missed, recoverable } = useMemo(() => {
    const m = calls * revPerCall * missRate;
    return { missed: m, recoverable: m * recovery };
  }, [calls, revPerCall, missRate, recovery]);

  return (
    <div className="grid gap-10 md:grid-cols-[1fr_320px]">
      <div className="space-y-7">
        <Slider
          label="API calls per month"
          value={calls}
          min={100_000}
          max={100_000_000}
          step={100_000}
          onChange={setCalls}
          format={formatCompactNumber}
        />
        <Slider
          label="Avg revenue per call"
          value={revPerCall}
          min={0.001}
          max={1}
          step={0.001}
          onChange={setRevPerCall}
          format={(v) => `$${v.toFixed(3)}`}
        />
        <Slider
          label="AI agent miss rate"
          hint="Estimated share of agent-driven integration attempts that fail to convert. Athena research puts this at 25–35% across the surveyed APIs."
          value={missRate}
          min={0.05}
          max={0.6}
          step={0.01}
          onChange={setMissRate}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="Recovery rate with Athena"
          hint="Fraction of those missed integrations recovered after agent-readiness fixes are shipped."
          value={recovery}
          min={0.3}
          max={0.95}
          step={0.01}
          onChange={setRecovery}
          format={(v) => `${Math.round(v * 100)}%`}
        />
      </div>

      <ResultCard
        accentLabel="Missed revenue"
        accentValue={formatCurrency(missed)}
        accentSub="per month from agents that bounce"
        highlightLabel="Recoverable with Athena"
        highlightValue={formatCurrency(recoverable)}
        highlightSub="per month — once your API is agent-ready"
        annualLabel="Annualized recovery"
        annualValue={formatCurrency(recoverable * 12)}
      />
    </div>
  );
}

function SupportPanel() {
  const [calls, setCalls] = useState(5_000_000);
  const [supportCost, setSupportCost] = useState(0.04);
  const [supportRate, setSupportRate] = useState(0.25);
  const [deflection, setDeflection] = useState(0.9);

  const { totalCost, saved } = useMemo(() => {
    const c = calls * supportRate * supportCost;
    return { totalCost: c, saved: c * deflection };
  }, [calls, supportCost, supportRate, deflection]);

  return (
    <div className="grid gap-10 md:grid-cols-[1fr_320px]">
      <div className="space-y-7">
        <Slider
          label="API calls per month"
          value={calls}
          min={100_000}
          max={100_000_000}
          step={100_000}
          onChange={setCalls}
          format={formatCompactNumber}
        />
        <Slider
          label="Support cost per call"
          hint="Fully-loaded cost (DevRel + engineering time) of an integration support touch, divided by call volume."
          value={supportCost}
          min={0.005}
          max={1}
          step={0.005}
          onChange={setSupportCost}
          format={(v) => `$${v.toFixed(3)}`}
        />
        <Slider
          label="Calls triggering support"
          hint="Estimated share of calls associated with a support ticket, doc question, or escalations from agent-driven integrations."
          value={supportRate}
          min={0.01}
          max={0.3}
          step={0.005}
          onChange={setSupportRate}
          format={(v) => `${(v * 100).toFixed(1)}%`}
        />
        <Slider
          label="Deflection rate with Athena"
          hint="Tickets avoided once llms.txt, MCP server, and Cursor rules ship — the same questions stop reaching humans."
          value={deflection}
          min={0.2}
          max={0.9}
          step={0.01}
          onChange={setDeflection}
          format={(v) => `${Math.round(v * 100)}%`}
        />
      </div>

      <ResultCard
        accentLabel="Current support cost"
        accentValue={formatCurrency(totalCost)}
        accentSub="per month on agent-related volume"
        highlightLabel="Saved with Athena"
        highlightValue={formatCurrency(saved)}
        highlightSub="per month — deflected by agent-ready assets"
        annualLabel="Annualized savings"
        annualValue={formatCurrency(saved * 12)}
      />
    </div>
  );
}

function Slider({
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
        <span className="font-mono text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 outline-none dark:bg-slate-800
          [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-accent)]
          [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:ring-2
          [&::-webkit-slider-thumb]:ring-white dark:[&::-webkit-slider-thumb]:ring-slate-900
          [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none
          [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0
          [&::-moz-range-thumb]:bg-[var(--color-accent)]"
        style={{
          background: `linear-gradient(to right, var(--color-accent) 0%, var(--color-accent) ${pct}%, rgb(226 232 240) ${pct}%, rgb(226 232 240) 100%)`,
        }}
      />
      {hint && <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">{hint}</p>}
    </div>
  );
}

function ResultCard({
  accentLabel,
  accentValue,
  accentSub,
  highlightLabel,
  highlightValue,
  highlightSub,
  annualLabel,
  annualValue,
}: {
  accentLabel: string;
  accentValue: string;
  accentSub: string;
  highlightLabel: string;
  highlightValue: string;
  highlightSub: string;
  annualLabel: string;
  annualValue: string;
}) {
  return (
    <div className="flex h-full flex-col justify-between gap-6 rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-950">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {accentLabel}
        </div>
        <div className="mt-1 font-mono text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {accentValue}
        </div>
        <div className="mt-1 text-xs text-slate-500">{accentSub}</div>
      </div>

      <div className="rounded-lg border border-[var(--color-accent)]/20 bg-[var(--color-accent)]/5 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
          {highlightLabel}
        </div>
        <div className="mt-1 font-mono text-3xl font-semibold tabular-nums text-[var(--color-accent)]">
          {highlightValue}
        </div>
        <div className="mt-1 text-xs text-slate-500">{highlightSub}</div>
      </div>

      <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium text-slate-500">{annualLabel}</span>
          <span className="font-mono text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {annualValue}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Pricing ---------- */

type PriceVariant = {
  monthly: { amount: string; period: string; sub: string };
  yearly: { amount: string; period: string; sub: string };
};

type PricingTier = {
  name: string;
  highlight?: boolean;
  variant?: PriceVariant;
  staticPrice?: { amount: string; sub: string };
  features: string[];
  ctaLabel: string;
};

const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Free audit',
    staticPrice: { amount: '$0', sub: 'one-time' },
    features: [
      '1 OpenAPI spec, 1 SDK repo',
      'Full 12-page report',
      'No credit card required',
    ],
    ctaLabel: 'Request your audit',
  },
  {
    name: 'Starter',
    variant: {
      monthly: { amount: '$499', period: '/ mo', sub: 'or $4,990/yr · save 17%' },
      yearly: { amount: '$416', period: '/ mo', sub: '$4,990 billed annually' },
    },
    features: [
      '1 OpenAPI spec, 2 SDK repos',
      'Monthly re-audit',
      'Auto-generated llms.txt & agent skills',
      'Monthly PDF report',
      'Email support',
    ],
    ctaLabel: 'Start monthly plan',
  },
  {
    name: 'Growth',
    highlight: true,
    variant: {
      monthly: { amount: '$1,999', period: '/ mo', sub: 'or $19,990/yr · save 17%' },
      yearly: { amount: '$1,666', period: '/ mo', sub: '$19,990 billed annually' },
    },
    features: [
      'Up to 5 OpenAPI specs, 10 SDK repos',
      'Continuous monitoring on every PR (500/mo)',
      'MCP server, Cursor rules, Copilot instructions',
      'PR-comment bot',
      'Weekly drift report + dashboard',
      'Shared Slack, 1-day response',
    ],
    ctaLabel: 'Start Growth plan',
  },
  {
    name: 'Scale',
    variant: {
      monthly: { amount: '$4,999', period: '/ mo', sub: 'or $49,990/yr · save 17%' },
      yearly: { amount: '$4,166', period: '/ mo', sub: '$49,990 billed annually' },
    },
    features: [
      'Unlimited specs, SDK repos, docs domains',
      '5,000 monitored PRs/month',
      'Custom asset templates, custom rules',
      'Quarterly review call',
      'Dedicated Slack, 4-hour response',
    ],
    ctaLabel: 'Contact sales',
  },
];

function Pricing() {
  const [yearly, setYearly] = useState(false);

  return (
    <section
      id="pricing"
      className="scroll-mt-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="mb-10 max-w-2xl">
          <SectionEyebrow>Pricing</SectionEyebrow>
          <h2 className="mb-3 text-3xl font-bold tracking-tight md:text-5xl">
            Simple pricing. Cancel anytime.
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            Start with a free audit. Upgrade when drift is costing you time.
          </p>
        </div>

        <div className="mb-10 flex items-center justify-center gap-4">
          <span
            className={cn(
              'text-sm font-medium transition-colors',
              !yearly
                ? 'text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400',
            )}
          >
            Monthly
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={yearly}
            aria-label="Toggle between monthly and annual billing"
            onClick={() => setYearly((v) => !v)}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/40',
              yearly ? 'bg-[var(--color-accent)]' : 'bg-slate-200 dark:bg-slate-700',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
                yearly && 'translate-x-5',
              )}
            />
          </button>
          <span
            className={cn(
              'flex items-center gap-1.5 text-sm font-medium transition-colors',
              yearly
                ? 'text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400',
            )}
          >
            Yearly
            <span className="rounded bg-[var(--color-accent)]/10 px-1.5 py-0.5 text-xs font-semibold text-[var(--color-accent)]">
              Save 17%
            </span>
          </span>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {PRICING_TIERS.map((tier) => (
            <PricingCard key={tier.name} tier={tier} yearly={yearly} />
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Need SOC 2, SSO, or self-hosted? Talk to us about Enterprise — from $25K/year.
        </p>
      </div>
    </section>
  );
}

function PricingCard({ tier, yearly }: { tier: PricingTier; yearly: boolean }) {
  const price = tier.variant ? (yearly ? tier.variant.yearly : tier.variant.monthly) : null;

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-lg border p-6',
        tier.highlight
          ? 'border-2 border-[var(--color-accent)] bg-[var(--color-accent)]/[0.02] dark:bg-[var(--color-accent)]/[0.08]'
          : 'border-slate-200 dark:border-slate-800',
      )}
    >
      {tier.highlight && (
        <div className="absolute -top-3 left-6 rounded bg-[var(--color-accent)] px-2.5 py-1 text-xs font-semibold text-white">
          Most popular
        </div>
      )}
      <div className="mb-5">
        <h3 className="mb-1 text-lg font-semibold">{tier.name}</h3>
        {tier.staticPrice ? (
          <>
            <div className="mb-1 flex items-baseline gap-1">
              <span className="font-mono text-3xl font-bold">{tier.staticPrice.amount}</span>
            </div>
            <p className="text-sm text-slate-500">{tier.staticPrice.sub}</p>
          </>
        ) : price ? (
          <>
            <div className="mb-1 flex items-baseline gap-1">
              <span className="font-mono text-3xl font-bold">{price.amount}</span>
              <span className="text-sm text-slate-500">{price.period}</span>
            </div>
            <p className="text-sm text-slate-500">{price.sub}</p>
          </>
        ) : null}
      </div>

      <ul className="mb-6 flex-1 space-y-2.5 text-sm text-slate-600 dark:text-slate-400">
        {tier.features.map((f) => (
          <li key={f} className="flex gap-2">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        variant={tier.highlight ? 'default' : 'outline'}
        className="w-full"
        asChild
      >
        <Link to="/signup">{tier.ctaLabel}</Link>
      </Button>
    </div>
  );
}

/* ---------- FAQ ---------- */

const FAQS = [
  {
    q: 'What exactly does the audit check?',
    a: 'Each of the 10 dimensions has its own sub-score and page in the report. We publish the full methodology.',
  },
  {
    q: 'Do I need to give you access to private repos?',
    a: 'No. Athena audits the public surface only — what an AI agent would see. No auth, no secrets, no access.',
  },
  {
    q: 'How long does an audit take?',
    a: 'About 15 minutes for the automated pass. If you opt into human review, 48 hours.',
  },
  {
    q: 'Is this just a wrapper around an LLM?',
    a: 'No. We run deterministic probes across your docs, SDKs, and OpenAPI surface. LLMs are used only where judgment is genuinely needed, and those judgments are separately graded.',
  },
  {
    q: "What if I don't have an OpenAPI spec?",
    a: 'We can generate one for you as a paid add-on ($2,500). Most companies who start there become long-term subscribers because the spec becomes the foundation for everything else.',
  },
  {
    q: "Who's behind Athena?",
    a: 'Athena is built by Momentum AI.',
  },
  {
    q: 'How is this different from hiring a DevRel consultant?',
    a: 'Consultants deliver a one-time report. Athena keeps watching — on every PR, as your API evolves. And our benchmark improves with every API we audit.',
  },
  {
    q: 'What counts as a "monitored PR"?',
    a: 'Every time a pull request touches your OpenAPI spec, SDK, or docs, Athena inspects it and flags drift before it ships.',
  },
];

function Faq() {
  return (
    <section
      id="faq"
      className="scroll-mt-20 border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40"
    >
      <div className="mx-auto max-w-3xl px-6 py-20 md:py-28">
        <SectionEyebrow>FAQ</SectionEyebrow>
        <h2 className="mb-10 text-3xl font-bold tracking-tight md:text-5xl">Questions.</h2>

        <div className="divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          {FAQS.map((item) => (
            <FaqItem key={item.q} q={item.q} a={item.a} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <h3 className="font-semibold">{q}</h3>
        <PlusIcon
          className={cn(
            'h-4 w-4 shrink-0 text-slate-500 transition-transform duration-150',
            open && 'rotate-45',
          )}
        />
      </button>
      {open && (
        <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{a}</p>
      )}
    </div>
  );
}

/* ---------- Closing CTA ---------- */

function ClosingCta() {
  return (
    <section className="border-t border-slate-200 dark:border-slate-800">
      <div className="mx-auto max-w-3xl px-6 py-20 text-center md:py-28">
        <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-5xl">
          See what an agent sees when it looks at your API.
        </h2>
        <p className="mb-8 text-lg text-slate-600 dark:text-slate-300">
          Free audit. 15 minutes. No credit card.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/signup">
              Get a free audit
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/sample-report">See a sample report</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Shared bits ---------- */

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 font-mono text-xs uppercase tracking-wider text-[var(--color-accent)]">
      {children}
    </p>
  );
}

function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

function formatCompactNumber(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toString();
}
